import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import STLViewer from '../components/STLViewer';
import { API, useAuth } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Upload, X, Plus, Minus, ChevronRight, FileText, Loader2, Mail, AlertTriangle, Save, Bookmark } from 'lucide-react';

const GetQuotePage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  // Track page visit
  useEffect(() => {
    const trackVisit = async () => {
      try {
        await axios.post(`${API}/visits/track`, { page: '/' });
      } catch (error) {
        // Silent fail - don't affect user experience
      }
    };
    trackVisit();
  }, []);
  const [files, setFiles] = useState([]);
  const [technologies, setTechnologies] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [postProcesses, setPostProcesses] = useState([]);
  const [deliveryOptions, setDeliveryOptions] = useState([]);
  const [selectedDelivery, setSelectedDelivery] = useState('');
  const [blanketMaterial, setBlanketMaterial] = useState('');
  const [uploading, setUploading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [quote, setQuote] = useState(null);
  const [selectedFileId, setSelectedFileId] = useState(null);
  const [savingQuote, setSavingQuote] = useState(false);
  const [saveQuoteOpen, setSaveQuoteOpen] = useState(false);
  const [saveQuoteEmail, setSaveQuoteEmail] = useState('');
  
  // File size error state
  const [fileSizeError, setFileSizeError] = useState({ open: false, fileName: '', fileSize: '' });
  
  // Request Quote Modal state
  const [requestQuoteOpen, setRequestQuoteOpen] = useState(false);
  const [requestingQuote, setRequestingQuote] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    notes: ''
  });

  // Check if any files need manual volume entry
  const hasManualVolumeFiles = files.some(f => f.volumeNeedsManualEntry);

  // Get the actual file object from the selected file ID
  const selectedFile = files.find(f => f.id === selectedFileId)?.file || null;

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const [techRes, matRes, procRes, delRes] = await Promise.all([
        axios.get(`${API}/settings/technologies`),
        axios.get(`${API}/settings/materials`),
        axios.get(`${API}/settings/post-processes`),
        axios.get(`${API}/settings/delivery-options`)
      ]);
      
      setTechnologies(techRes.data.technologies || []);
      setMaterials(matRes.data.materials || []);
      setPostProcesses(procRes.data.post_processes || []);
      setDeliveryOptions(delRes.data.delivery_options || []);
      
      // Set default delivery
      if (delRes.data.delivery_options?.length > 0) {
        setSelectedDelivery(delRes.data.delivery_options[0].delivery_id);
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
      toast.error('Failed to load pricing settings');
    }
  };

  const handleFileUpload = async (e) => {
    const uploadedFiles = Array.from(e.target.files);
    setUploading(true);

    const validExtensions = ['.stl', '.obj', '.3mf', '.step', '.stp', '.iges', '.igs'];
    const MAX_FILE_SIZE_MB = 50; // Maximum file size in MB
    const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

    for (const file of uploadedFiles) {
      const fileExt = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      if (!validExtensions.includes(fileExt)) {
        toast.error(`${file.name} - unsupported format`);
        continue;
      }

      // Check file size
      if (file.size > MAX_FILE_SIZE_BYTES) {
        const fileSizeMB = (file.size / 1024 / 1024).toFixed(1);
        setFileSizeError({ 
          open: true, 
          fileName: file.name, 
          fileSize: fileSizeMB 
        });
        continue;
      }

      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API}/quote/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        const defaultTech = technologies.find(t => t.active)?.tech_id || '';
        const defaultMat = materials.find(m => m.technology_id === defaultTech)?.material_id || blanketMaterial;

        const newFile = {
          id: response.data.file_id,
          name: response.data.filename,
          volume: response.data.volume_cm3,
          file: file,
          fileType: response.data.file_type || 'stl',
          volumeNeedsManualEntry: response.data.volume_needs_manual_entry || false,
          technology: defaultTech,
          material: defaultMat || defaultTech ? materials.find(m => m.technology_id === defaultTech)?.material_id : '',
          postProcesses: [],
          quantity: 1
        };

        // Warn user if volume needs manual entry
        if (response.data.volume_needs_manual_entry) {
          toast.info(`${file.name}: STEP/IGES file - will require manual quote`, { duration: 5000 });
        }

        setFiles(prev => [...prev, newFile]);

        // Auto-select the newly uploaded file for preview (if it's previewable)
        const previewableExtensions = ['.stl', '.step', '.stp', '.iges', '.igs'];
        if (previewableExtensions.includes(fileExt)) {
          setSelectedFileId(response.data.file_id);
        }
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    setUploading(false);
    e.target.value = '';
  };

  const removeFile = (id) => {
    setFiles(prev => prev.filter(f => f.id !== id));
    if (selectedFileId === id) {
      const remaining = files.filter(f => f.id !== id);
      setSelectedFileId(remaining[0]?.id || null);
    }
  };

  const updateFile = (id, field, value) => {
    setFiles(prev => prev.map(f => {
      if (f.id === id) {
        const updated = { ...f, [field]: value };
        // Reset material when technology changes
        if (field === 'technology') {
          const availableMats = materials.filter(m => m.technology_id === value);
          updated.material = availableMats[0]?.material_id || '';
          updated.postProcesses = [];
        }
        return updated;
      }
      return f;
    }));
  };

  const applyBlanketMaterial = () => {
    if (!blanketMaterial) return;
    
    const mat = materials.find(m => m.material_id === blanketMaterial);
    if (!mat) return;

    setFiles(prev => prev.map(f => ({
      ...f,
      technology: mat.technology_id,
      material: blanketMaterial,
      postProcesses: []
    })));
    
    toast.success('Applied material to all files');
  };

  const calculateQuote = async () => {
    if (files.length === 0) {
      toast.error('Please upload at least one file');
      return;
    }

    // Check for files with zero or invalid volume (only for non-manual files)
    const autoVolumeFiles = files.filter(f => !f.volumeNeedsManualEntry);
    const invalidVolumeFiles = autoVolumeFiles.filter(f => !f.volume || f.volume <= 0);
    if (invalidVolumeFiles.length > 0) {
      toast.error(`Invalid volume for: ${invalidVolumeFiles.map(f => f.name).join(', ')}`);
      return;
    }

    setCalculating(true);
    try {
      const items = files.map(f => ({
        file_id: f.id,  // Include file_id for downloads
        file_name: f.name,
        volume_cm3: f.volume,
        technology_id: f.technology,
        material_id: f.material,
        post_process_ids: f.postProcesses,
        quantity: f.quantity
      }));

      const response = await axios.post(`${API}/quote/calculate`, {
        items,
        delivery_option_id: selectedDelivery
      });

      setQuote(response.data);
      toast.success('Quote calculated!');
    } catch (error) {
      toast.error('Failed to calculate quote');
    }
    setCalculating(false);
  };

  const submitQuoteRequest = async () => {
    if (!customerInfo.email) {
      toast.error('Please enter your email address');
      return;
    }

    setRequestingQuote(true);
    try {
      const response = await axios.post(`${API}/quote/request`, {
        customer_name: customerInfo.name,
        customer_email: customerInfo.email,
        customer_phone: customerInfo.phone,
        notes: customerInfo.notes,
        files: files.map(f => ({
          id: f.id,
          name: f.name,
          fileType: f.fileType,
          volume: f.volume || 0,
          volumeNeedsManualEntry: f.volumeNeedsManualEntry,
          technology: f.technology,
          material: f.material,
          postProcesses: f.postProcesses,
          quantity: f.quantity
        })),
        delivery_option_id: selectedDelivery
      });

      toast.success(response.data.message);
      setRequestQuoteOpen(false);
      
      // Show success message and clear form
      setFiles([]);
      setQuote(null);
      setCustomerInfo({ name: '', email: '', phone: '', notes: '' });
      
      // Navigate to a thank you state or show confirmation
      toast.success(`Your request ${response.data.request_id} has been submitted! We'll email you shortly.`, { duration: 8000 });
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit quote request');
    }
    setRequestingQuote(false);
  };

  const proceedToCheckout = () => {
    if (!quote) return;
    
    // Store quote in session storage
    sessionStorage.setItem('quote', JSON.stringify(quote));
    sessionStorage.setItem('quoteFiles', JSON.stringify(files));
    navigate('/checkout');
  };

  const handleSaveQuote = async () => {
    if (!quote) return;
    
    // If not authenticated and no email, show dialog
    if (!isAuthenticated && !saveQuoteEmail) {
      setSaveQuoteOpen(true);
      return;
    }
    
    setSavingQuote(true);
    try {
      const headers = {};
      if (isAuthenticated) {
        headers.Authorization = `Bearer ${localStorage.getItem('auth_token')}`;
      }
      
      await axios.post(`${API}/quotes/save`, {
        quote: quote,
        customer_email: isAuthenticated ? user?.email : saveQuoteEmail
      }, { headers });
      
      toast.success('Quote saved! Access it from your dashboard.');
      setSaveQuoteOpen(false);
      setSaveQuoteEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save quote');
    }
    setSavingQuote(false);
  };

  const getMaterialsForTech = (techId) => {
    return materials.filter(m => m.technology_id === techId);
  };

  const getProcessesForMaterial = (matId) => {
    return postProcesses.filter(p => p.material_id === matId);
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="get-quote-page">
      <Navbar />
      
      <main className="pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Header */}
          <div className="mb-12">
            <span className="overline text-[#CCFF00]">Instant Quote</span>
            <h1 className="text-3xl sm:text-4xl font-['Chivo'] font-black text-white mt-2">
              Upload Your Parts
            </h1>
            <p className="text-white/50 mt-2">
              Upload STL files to get instant pricing. Select materials and options for each part.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Left Column - Upload & Files */}
            <div className="lg:col-span-7 space-y-6">
              {/* 3D Preview */}
              <div className="bg-[#111] border border-white/10 rounded-sm p-4">
                <STLViewer file={selectedFile} fileId={selectedFileId} height={350} />
              </div>

              {/* Upload Zone */}
              <div className="relative">
                <input
                  type="file"
                  multiple
                  accept=".stl,.obj,.3mf,.step,.stp,.iges,.igs"
                  onChange={handleFileUpload}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  disabled={uploading}
                  data-testid="file-upload-input"
                />
                <div className="border-2 border-dashed border-white/20 hover:border-[#CCFF00]/50 transition-colors rounded-sm p-8 text-center">
                  {uploading ? (
                    <Loader2 className="w-8 h-8 text-[#CCFF00] mx-auto animate-spin" />
                  ) : (
                    <Upload className="w-8 h-8 text-[#CCFF00] mx-auto mb-3" />
                  )}
                  <p className="text-white font-medium">
                    {uploading ? 'Uploading...' : 'Drop 3D files here or click to upload'}
                  </p>
                  <p className="text-white/50 text-sm mt-1">STL, OBJ, 3MF, STEP, IGES</p>
                </div>
              </div>

              {/* Blanket Material Selection */}
              {files.length > 1 && (
                <div className="bg-[#111] border border-white/10 rounded-sm p-4">
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <Label className="text-white/70 text-sm">Apply material to all files</Label>
                      <Select value={blanketMaterial} onValueChange={setBlanketMaterial}>
                        <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                          <SelectValue placeholder="Select material..." />
                        </SelectTrigger>
                        <SelectContent className="bg-[#111] border-white/10">
                          {materials.map(mat => (
                            <SelectItem key={mat.material_id} value={mat.material_id} className="text-white">
                              {mat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button 
                      onClick={applyBlanketMaterial}
                      disabled={!blanketMaterial}
                      className="bg-white/10 text-white hover:bg-white/20 mt-6"
                      data-testid="apply-blanket-btn"
                    >
                      Apply to All
                    </Button>
                  </div>
                </div>
              )}

              {/* File List */}
              <div className="space-y-4">
                {files.map((file, index) => (
                  <div 
                    key={file.id}
                    className={`bg-[#111] border rounded-sm p-4 cursor-pointer transition-colors ${
                      selectedFileId === file.id ? 'border-[#CCFF00]' : 'border-white/10 hover:border-white/20'
                    }`}
                    onClick={() => setSelectedFileId(file.id)}
                    data-testid={`file-item-${index}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-[#CCFF00]" />
                        <div>
                          <p className="text-white font-medium">{file.name}</p>
                          {file.volumeNeedsManualEntry ? (
                            <div className="flex items-center gap-2 mt-1" onClick={e => e.stopPropagation()}>
                              <Input
                                type="number"
                                step="0.01"
                                min="0.01"
                                value={file.volume || ''}
                                onChange={(e) => updateFile(file.id, 'volume', parseFloat(e.target.value) || 0)}
                                placeholder="Enter volume"
                                className="w-28 h-7 bg-yellow-500/10 border-yellow-500/30 text-yellow-400 text-sm font-mono"
                              />
                              <span className="text-yellow-400 text-sm">cm³</span>
                              <span className="text-yellow-400/70 text-xs">(manual)</span>
                            </div>
                          ) : (
                            <p className="text-white/50 text-sm font-mono">
                              {file.volume.toFixed(2)} cm³
                            </p>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); removeFile(file.id); }}
                        className="text-white/50 hover:text-red-400 transition-colors"
                        data-testid={`remove-file-${index}`}
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-4" onClick={e => e.stopPropagation()}>
                      {/* Technology */}
                      <div>
                        <Label className="text-white/50 text-xs">Technology</Label>
                        <Select 
                          value={file.technology} 
                          onValueChange={(v) => updateFile(file.id, 'technology', v)}
                        >
                          <SelectTrigger className="bg-white/5 border-white/10 text-white text-sm h-9 mt-1">
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent className="bg-[#111] border-white/10">
                            {technologies.filter(t => t.active).map(tech => (
                              <SelectItem key={tech.tech_id} value={tech.tech_id} className="text-white">
                                {tech.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Material */}
                      <div>
                        <Label className="text-white/50 text-xs">Material</Label>
                        <Select 
                          value={file.material} 
                          onValueChange={(v) => updateFile(file.id, 'material', v)}
                        >
                          <SelectTrigger className="bg-white/5 border-white/10 text-white text-sm h-9 mt-1">
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent className="bg-[#111] border-white/10">
                            {getMaterialsForTech(file.technology).map(mat => (
                              <SelectItem key={mat.material_id} value={mat.material_id} className="text-white">
                                {mat.name} (${mat.price_per_cm3}/cm³)
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Quantity */}
                      <div>
                        <Label className="text-white/50 text-xs">Quantity</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <button
                            onClick={() => updateFile(file.id, 'quantity', Math.max(1, file.quantity - 1))}
                            className="w-9 h-9 bg-white/5 border border-white/10 text-white flex items-center justify-center hover:bg-white/10"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <Input
                            type="number"
                            min="1"
                            value={file.quantity}
                            onChange={(e) => updateFile(file.id, 'quantity', Math.max(1, parseInt(e.target.value) || 1))}
                            className="w-16 bg-white/5 border-white/10 text-white text-center h-9"
                          />
                          <button
                            onClick={() => updateFile(file.id, 'quantity', file.quantity + 1)}
                            className="w-9 h-9 bg-white/5 border border-white/10 text-white flex items-center justify-center hover:bg-white/10"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Post Processing */}
                      <div>
                        <Label className="text-white/50 text-xs">Post Processing</Label>
                        <Select 
                          value={file.postProcesses[0] || 'none'} 
                          onValueChange={(v) => updateFile(file.id, 'postProcesses', v === 'none' ? [] : [v])}
                        >
                          <SelectTrigger className="bg-white/5 border-white/10 text-white text-sm h-9 mt-1">
                            <SelectValue placeholder="None" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#111] border-white/10">
                            <SelectItem value="none" className="text-white">None</SelectItem>
                            {getProcessesForMaterial(file.material).map(proc => (
                              <SelectItem key={proc.process_id} value={proc.process_id} className="text-white">
                                {proc.name} (+${proc.price_per_unit})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                ))}

                {files.length === 0 && (
                  <div className="text-center py-12 text-white/30">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No files uploaded yet</p>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Quote Summary */}
            <div className="lg:col-span-5">
              <div className="sticky top-24 bg-[#111] border border-white/10 rounded-sm p-6">
                <h2 className="text-xl font-bold text-white mb-6">Quote Summary</h2>

                {/* Delivery Selection */}
                <div className="mb-6">
                  <Label className="text-white/70">Delivery Option</Label>
                  <Select value={selectedDelivery} onValueChange={setSelectedDelivery}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white mt-2">
                      <SelectValue placeholder="Select delivery..." />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111] border-white/10">
                      {deliveryOptions.map(opt => (
                        <SelectItem key={opt.delivery_id} value={opt.delivery_id} className="text-white">
                          {opt.name} - ${opt.price.toFixed(2)} ({opt.estimated_days} days)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Show notice if STEP/IGES files are present */}
                {hasManualVolumeFiles && (
                  <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-sm">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-yellow-400 font-medium text-sm">STEP/IGES Files Detected</p>
                        <p className="text-yellow-400/70 text-xs mt-1">
                          These files require manual volume calculation. Click "Request Quote" and we'll review your files and email you a quote.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Show either Calculate Quote or Request Quote button */}
                {hasManualVolumeFiles ? (
                  <Button
                    onClick={() => setRequestQuoteOpen(true)}
                    disabled={files.length === 0}
                    className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold mb-6"
                    data-testid="request-quote-btn"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Request Quote
                  </Button>
                ) : (
                  <Button
                    onClick={calculateQuote}
                    disabled={files.length === 0 || calculating}
                    className="w-full bg-white/10 text-white hover:bg-white/20 mb-6"
                    data-testid="calculate-quote-btn"
                  >
                    {calculating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Calculating...
                      </>
                    ) : (
                      'Calculate Quote'
                    )}
                  </Button>
                )}

                {/* Quote Details */}
                {quote && (
                  <div className="space-y-4" data-testid="quote-summary">
                    <div className="border-t border-white/10 pt-4 space-y-3">
                      {quote.items?.map((item, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span className="text-white/70 truncate max-w-[60%]">
                            {item.file_name} x{item.quantity}
                          </span>
                          <span className="text-white font-mono">
                            ${item.total_price.toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>

                    <div className="border-t border-white/10 pt-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-white/50">Subtotal</span>
                        <span className="text-white font-mono">${quote.subtotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-white/50">Delivery</span>
                        <span className="text-white font-mono">${quote.delivery_cost.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-lg pt-2 border-t border-white/10">
                        <span className="text-white font-bold">Total</span>
                        <span className="text-[#CCFF00] font-bold font-mono">
                          ${quote.total.toFixed(2)} AUD
                        </span>
                      </div>
                    </div>

                    <Button
                      onClick={proceedToCheckout}
                      className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide rounded-none py-6 neon-glow"
                      data-testid="proceed-checkout-btn"
                    >
                      Proceed to Checkout
                      <ChevronRight className="w-5 h-5 ml-2" />
                    </Button>
                    
                    <Button
                      onClick={() => isAuthenticated ? handleSaveQuote() : setSaveQuoteOpen(true)}
                      variant="outline"
                      className="w-full border-white/20 text-white hover:bg-white/5 rounded-none py-5 mt-3"
                      disabled={savingQuote}
                    >
                      {savingQuote ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Bookmark className="w-4 h-4 mr-2" />
                      )}
                      Save Quote for Later
                    </Button>
                  </div>
                )}

                {!quote && !hasManualVolumeFiles && (
                  <div className="text-center text-white/30 py-8">
                    <p className="text-sm">
                      Upload files and click Calculate Quote to see pricing
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Request Quote Dialog */}
      <Dialog open={requestQuoteOpen} onOpenChange={setRequestQuoteOpen}>
        <DialogContent className="bg-[#111] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Mail className="w-5 h-5 text-[#CCFF00]" />
              Request a Quote
            </DialogTitle>
            <DialogDescription className="text-white/50">
              We'll review your STEP/IGES files and email you a detailed quote within 24 hours.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-white/70">Your Name</Label>
              <Input
                value={customerInfo.name}
                onChange={(e) => setCustomerInfo(prev => ({ ...prev, name: e.target.value }))}
                placeholder="John Smith"
                className="bg-white/5 border-white/10 text-white mt-1"
              />
            </div>
            
            <div>
              <Label className="text-white/70">Email Address *</Label>
              <Input
                type="email"
                value={customerInfo.email}
                onChange={(e) => setCustomerInfo(prev => ({ ...prev, email: e.target.value }))}
                placeholder="john@example.com"
                className="bg-white/5 border-white/10 text-white mt-1"
                required
              />
            </div>
            
            <div>
              <Label className="text-white/70">Phone Number</Label>
              <Input
                type="tel"
                value={customerInfo.phone}
                onChange={(e) => setCustomerInfo(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+61 400 000 000"
                className="bg-white/5 border-white/10 text-white mt-1"
              />
            </div>
            
            <div>
              <Label className="text-white/70">Additional Notes</Label>
              <Textarea
                value={customerInfo.notes}
                onChange={(e) => setCustomerInfo(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any special requirements, deadlines, or questions..."
                className="bg-white/5 border-white/10 text-white mt-1 min-h-[80px]"
              />
            </div>

            {/* Files Summary */}
            <div className="bg-white/5 p-3 rounded-sm">
              <p className="text-white/50 text-xs mb-2">FILES TO QUOTE ({files.length})</p>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-white truncate max-w-[70%]">{f.name}</span>
                    <span className="text-white/50">x{f.quantity}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <Button
              onClick={submitQuoteRequest}
              disabled={requestingQuote || !customerInfo.email}
              className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold"
            >
              {requestingQuote ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Submit Quote Request
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Save Quote Dialog */}
      <Dialog open={saveQuoteOpen} onOpenChange={setSaveQuoteOpen}>
        <DialogContent className="bg-[#111] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Bookmark className="w-5 h-5 text-[#CCFF00]" />
              Save Quote for Later
            </DialogTitle>
            <DialogDescription className="text-white/50">
              {isAuthenticated 
                ? "Your quote will be saved to your account for 30 days."
                : "Enter your email to save this quote. Create an account later to access it."
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {!isAuthenticated && (
              <div>
                <Label className="text-white/70">Email Address *</Label>
                <Input
                  type="email"
                  value={saveQuoteEmail}
                  onChange={(e) => setSaveQuoteEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
                <p className="text-white/40 text-xs mt-1">
                  Register with this email to access your saved quotes
                </p>
              </div>
            )}

            {/* Quote Summary */}
            {quote && (
              <div className="bg-white/5 p-3 rounded-sm">
                <p className="text-white/50 text-xs mb-2">QUOTE SUMMARY</p>
                <div className="flex justify-between text-sm">
                  <span className="text-white/70">{quote.items?.length || 0} items</span>
                  <span className="text-[#CCFF00] font-bold">${quote.total?.toFixed(2)} AUD</span>
                </div>
              </div>
            )}
            
            <Button
              onClick={handleSaveQuote}
              disabled={savingQuote || (!isAuthenticated && !saveQuoteEmail)}
              className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold"
            >
              {savingQuote ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Quote
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* File Size Error Dialog */}
      <Dialog open={fileSizeError.open} onOpenChange={(open) => setFileSizeError({ ...fileSizeError, open })}>
        <DialogContent className="bg-[#111] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl text-red-400 flex items-center gap-2">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              File Too Large
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-white/80">
              <span className="font-semibold text-white">{fileSizeError.fileName}</span> is <span className="text-red-400 font-bold">{fileSizeError.fileSize}MB</span>
            </p>
            <p className="text-white/80">
              Maximum upload size is <span className="text-[#CCFF00] font-bold">50MB</span>
            </p>
            <div className="bg-[#CCFF00]/10 border border-[#CCFF00]/30 rounded-lg p-4 mt-4">
              <p className="text-[#CCFF00] font-semibold mb-2">For larger files:</p>
              <p className="text-white/80">
                Please send via Dropbox to<br/>
                <a href="mailto:hello@3dprintbureau.com.au" className="text-[#CCFF00] hover:underline font-semibold">
                  hello@3dprintbureau.com.au
                </a>
              </p>
            </div>
          </div>
          <Button 
            onClick={() => setFileSizeError({ open: false, fileName: '', fileSize: '' })}
            className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold"
          >
            Got it
          </Button>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default GetQuotePage;
